import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  Card,
  CardContent,
  Backdrop,
  CircularProgress,
  Stack,
  Alert,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  addSuite,
  getSuiteById,
  updateSuite,
} from "../../../../../features/slices/Suite";
import { FrameworkPicker } from "./FrameworkPicker";
import CasesPicker from "./CasesPicker";
import ResourcesPicker from "./ResourcesPicker";
import ReviewSubmit from "./ReviewSubmit";
import {
  processEmulatorResourceStorage,
  processFrameworkArtifact,
  processSourceArtifact,
} from "../../../../../api/registry/registry";
import { SuiteTagEditor } from "../SuiteTagEditor";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../../../../context/SnackbarContext";

const steps = ["Suite Title", "Framework", "Cases", "Resources", "Review"];

const StepCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>{children}</CardContent>
  </Card>
);

export default function WizardStorageSuite() {
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [suiteTitle, setSuiteTitle] = useState("");
  const [suiteId, setSuiteId] = useState(""); // from backend
  const [framework, setFramework] = useState<any>(null);
  const [cases, setCases] = useState<any>(null);
  const [resources, setResources] = useState<any>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { selectedId } = useParams();

  // Resume an existing (draft) storage suite: load it and jump to the
  // Framework step, mirroring the Git wizard's resume behaviour.
  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      setLoading(true);
      try {
        const result = await (dispatch as any)(
          getSuiteById(Number(selectedId))
        ).unwrap();
        if (result?.id) {
          setSuiteId(String(result.id));
          setSuiteTitle(result.title || "");
          setActiveStep(1);
        }
      } catch (e) {
        setError("Failed to load draft suite.");
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedId, dispatch]);

  const handleSuiteCreate = async () => {
    setLoading(true);
    setError("");
    try {
      const resultAction = await dispatch(addSuite({ title: suiteTitle }));
      const suiteData = resultAction.payload;
      if (suiteData?.id) {
        setSuiteId(suiteData.id);
        // Tag the suite as a storage/artifact draft so "Resume" reopens THIS
        // wizard (not the Git one), even if abandoned before picking cases.
        (dispatch as any)(
          updateSuite({
            id: Number(suiteData.id),
            title: suiteTitle,
            meta_data: { config: { suite: { source: "storage" } } },
          })
        );
        setActiveStep((s) => s + 1);
      } else {
        setError("Failed to create suite.");
      }
    } catch (e) {
      setError("Error creating suite.");
    } finally {
      setLoading(false);
    }
  };

  const handleFrameworkPick = async (item) => {
    if (!suiteId) return;
    setLoading(true);
    setError("");
    try {
      let payload = { suite_id: suiteId };
      if (item.type === "storage") {
        payload = {
          ...payload,
          type: "storage",
          bucket: item.bucket,
          path: item.path,
        };
      } else if (item.type === "url") {
        payload = { ...payload, type: "url", url: item.url };
      }
      const result = await processFrameworkArtifact(payload);
      if (result.ok || result.zip_path) {
        setFramework(item);
        setActiveStep((s) => s + 1);
      } else if (result.error) {
        setError(result.error || "Failed to process framework artifact");
      } else {
        setError("Failed to process framework artifact");
      }
    } catch (e) {
      setError("Error processing framework artifact");
    } finally {
      setLoading(false);
    }
  };

  const handleCasesPick = async (item) => {
    if (!suiteId) return;
    setLoading(true);
    setError("");
    try {
      const payload = {
        suite_id: suiteId,
        artifact_name: item.artifact_name,
        selected_tests: item.selected_tests,
      };
      if (item.artifact.type === "storage") {
        payload.bucket = item.artifact.bucket;
        payload.path = item.artifact.path;
      } else if (item.artifact.type === "url") {
        payload.url = item.artifact.url;
      }
      await processSourceArtifact(payload);
      setCases(item.artifact);
      setActiveStep((s) => s + 1);
    } catch (e) {
      setError("Error processing test cases");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmuResource = async (selectedList: any[]) => {
    const list = Array.isArray(selectedList) ? selectedList : selectedList ? [selectedList] : [];
    setLoading(true);
    setError("");
    try {
      // Each call unpacks + re-zips the single framework zip and appends the
      // emulator, so they MUST run sequentially — concurrent calls would race
      // on the framework zip. Process the chosen resources one by one, in order.
      for (const selected of list) {
        await processEmulatorResourceStorage({
          suite_id: suiteId,
          bucket: selected.bucket,
          path: selected.path,
          asset_name: selected.name,
        });
      }
      setResources(list);
      setActiveStep((s) => s + 1);
    } catch (e) {
      setError("Error processing emulator resource");
    } finally {
      setLoading(false);
    }
  };

  const handleSkipResources = () => {
    setResources(null);
    setActiveStep((s) => s + 1);
  };

  const handleSubmit = () => {
    showSnackbar("Suite created successfully!", "success");
    navigate("/suites");
    setActiveStep(0);
    setSuiteTitle("");
    setSuiteId("");
    setFramework(null);
    setCases(null);
    setResources(null);
  };

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", width: "100%" }}>
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography
          component="h2"
          sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 800, lineHeight: 1.2 }}
        >
          Import from artifact storage
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          Build a suite from framework and case artifacts already available in storage.
        </Typography>
      </Box>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: { xs: 2.5, md: 4 },
          overflowX: "auto",
          pb: 0.5,
          "& .MuiStepLabel-label": {
            fontSize: { xs: 12, sm: 13 },
            mt: 0.75,
          },
        }}
      >
        {steps.map((label, i) => (
          <Step key={i}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Backdrop open={loading} sx={{ color: "#fff", zIndex: 1200 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {activeStep === 0 && (
        <Card variant="outlined" sx={{ borderRadius: 1 }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
                  Name the suite
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
                  This name appears in the suite list before you select artifacts.
                </Typography>
              </Box>
            <TextField
              fullWidth
              label="Suite Title"
              value={suiteTitle}
              onChange={(e) => setSuiteTitle(e.target.value)}
                placeholder="Example: Cloud artifact regression"
              disabled={loading}
            />
            {error && (
                <Typography color="error">
                {error}
              </Typography>
            )}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleSuiteCreate}
                  disabled={!suiteTitle.trim() || loading}
                  sx={{ minWidth: { xs: "100%", sm: 120 } }}
                >
                  Next
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {error && activeStep !== 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {activeStep === 1 && (
        <StepCard>
          <FrameworkPicker fileType="any" onSelect={handleFrameworkPick} />
        </StepCard>
      )}

      {activeStep === 2 && (
        <StepCard>
          <CasesPicker suiteId={suiteId} onPick={handleCasesPick} />
        </StepCard>
      )}

      {activeStep === 3 && (
        <StepCard>
          <ResourcesPicker onPick={async (selected) => await handleAddEmuResource(selected)} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary">
              No extra resource needed? You can skip this step.
            </Typography>
            <Button variant="text" onClick={handleSkipResources} sx={{ textTransform: "none", fontWeight: 700 }}>
              Skip this step
            </Button>
          </Box>
        </StepCard>
      )}

      {activeStep === 4 && (
        <StepCard>
          <ReviewSubmit
            title={suiteTitle}
            framework={framework}
            cases={cases}
            resources={resources}
            onSubmit={handleSubmit}
          />
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <SuiteTagEditor suiteId={suiteId} />
          </Box>
        </StepCard>
      )}
    </Box>
  );
}
